/**
 * Predefinições de listagem em localStorage.
 *
 * Uma predefinição guarda o conjunto "colunas visíveis + filtros + período" com um nome, para o
 * usuário alternar entre recortes de trabalho sem remontar tudo. Além das predefinições salvas, o
 * repositório guarda o `estado` corrente (rascunho) — é o que faz a tela voltar como estava depois
 * de um F5, mesmo sem nada salvo.
 *
 * A API é deliberadamente pequena e sem dependência de Vue: trocar localStorage por um endpoint
 * (predefinições compartilhadas entre usuários) é reescrever só este arquivo.
 */

const VERSAO = 1;

function agora() {
    return new Date().toISOString();
}

function novoId() {
    return `v_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function envelopeVazio() {
    return {
        version: VERSAO,
        ativaId: null,
        estado: null,
        itens: [],
    };
}

/**
 * Comparação estável, insensível à ordem das chaves — o "sujo" da predefinição depende disso.
 */
function estavel(valor) {
    if (Array.isArray(valor)) {
        return valor.map(estavel);
    }

    if (valor && typeof valor === 'object') {
        return Object.keys(valor)
            .sort()
            .reduce((acc, chave) => {
                acc[chave] = estavel(valor[chave]);
                return acc;
            }, {});
    }

    return valor;
}

export function mesmoEstado(a, b) {
    return JSON.stringify(estavel(a)) === JSON.stringify(estavel(b));
}

export function criarRepositorio(chave) {
    function ler() {
        let bruto;

        try {
            bruto = JSON.parse(localStorage.getItem(chave));
        } catch (e) {
            bruto = null;
        }

        if (!bruto || bruto.version !== VERSAO || !Array.isArray(bruto.itens)) {
            return envelopeVazio();
        }

        return bruto;
    }

    function gravar(envelope) {
        try {
            localStorage.setItem(chave, JSON.stringify(envelope));
        } catch (e) {
            // Cota estourada ou storage indisponível: a tela continua funcionando sem persistir.
            console.warn('Não foi possível gravar as predefinições', e);
        }

        return envelope;
    }

    function atualizarEnvelope(fn) {
        const envelope = ler();
        fn(envelope);
        return gravar(envelope);
    }

    return {
        listar() {
            return ler().itens;
        },

        obter(id) {
            return ler().itens.find(i => i.id === id) || null;
        },

        ativaId() {
            return ler().ativaId;
        },

        lerEstado() {
            return ler().estado;
        },

        salvarEstado(estado) {
            return atualizarEnvelope(envelope => {
                envelope.estado = estado;
            });
        },

        definirAtiva(id) {
            return atualizarEnvelope(envelope => {
                envelope.ativaId = id;
            });
        },

        nomeEmUso(nome, ignorarId) {
            const alvo = String(nome || '').trim().toLowerCase();

            return ler().itens.some(i => i.id !== ignorarId && i.nome.trim().toLowerCase() === alvo);
        },

        criar(nome, estado) {
            const item = {
                id: novoId(),
                nome: String(nome).trim(),
                colunas: estado.colunas,
                filtros: estado.filtros,
                periodo: estado.periodo,
                criadoEm: agora(),
                atualizadoEm: agora(),
            };

            atualizarEnvelope(envelope => {
                envelope.itens.push(item);
                envelope.ativaId = item.id;
                envelope.estado = estado;
            });

            return item;
        },

        atualizar(id, estado) {
            let atualizado = null;

            atualizarEnvelope(envelope => {
                const item = envelope.itens.find(i => i.id === id);

                if (!item) {
                    return;
                }

                item.colunas = estado.colunas;
                item.filtros = estado.filtros;
                item.periodo = estado.periodo;
                item.atualizadoEm = agora();

                envelope.estado = estado;
                atualizado = item;
            });

            return atualizado;
        },

        renomear(id, nome) {
            return atualizarEnvelope(envelope => {
                const item = envelope.itens.find(i => i.id === id);

                if (item) {
                    item.nome = String(nome).trim();
                    item.atualizadoEm = agora();
                }
            });
        },

        duplicar(id, nome) {
            const original = this.obter(id);

            if (!original) {
                return null;
            }

            return this.criar(nome, {
                colunas: original.colunas,
                filtros: original.filtros,
                periodo: original.periodo,
            });
        },

        remover(id) {
            return atualizarEnvelope(envelope => {
                envelope.itens = envelope.itens.filter(i => i.id !== id);

                if (envelope.ativaId === id) {
                    envelope.ativaId = null;
                }
            });
        },
    };
}
